<template>
  <c-box>
    <form novalidate @submit.prevent="submit">
      <c-box :mx="{ md: '4xl' }" :pt="{ md: 'xl' }" font-size="md">
        <c-heading
          as="h3"
          size="sm"
          font-weight="bold"
          :mt="{ base: 'md', md: '3xl' }"
          :mb="{ base: 'md', md: 'lg' }"
        >
          {{ $t("productReturn.maintenanceRequest.actionsNeededTitle") }}
        </c-heading>
        <c-text
          v-if="$t('productReturn.maintenanceRequest.actionsNeededParagraph1')"
          :mb="{ base: 'md', md: 'lg' }"
        >
          {{ beautify($t("productReturn.maintenanceRequest.actionsNeededParagraph1")) }}
        </c-text>
        <c-text :mb="{ base: 'md', md: 'lg' }">
          {{ beautify($t("productReturn.maintenanceRequest.actionsNeededParagraph2")) }}
          <a :href="'/files/support/' + $t('productReturn.common.resetURI')" target="_blank">
            {{ $t("productReturn.returnDetails.resetInstructions") }}
          </a>
        </c-text>
        <c-text
          v-if="$t('productReturn.maintenanceRequest.actionsNeededParagraph3')"
          :mb="{ base: 'lg', md: '3rem' }"
        >
          {{ beautify($t("productReturn.maintenanceRequest.actionsNeededParagraph3")) }}
        </c-text>
        <c-text :mb="{ base: 'md', md: '3rem' }">{{
          $t("productReturn.maintenanceRequest.subtitle")
        }}</c-text>
      </c-box>

      <c-box v-for="i in form.sites.length" :key="'site_editor_' + i">
        <RMACRSiteEditor
          v-model="form.sites[i - 1]"
          :index="i"
          :count="form.sites.length"
          :pro-forma-required="proFormaRequired"
          :duplicate-serials="duplicateSerials"
          @onRemove="onRemove(i)"
          @errors="onErrors(i, $event)"
          v-chakra
          mb="2xl"
        />
      </c-box>
      <c-button
        :disabled="!firstSiteExists"
        @click="addNewSite()"
        data-cy="cr-add-new-site"
        border="1px dashed"
        border-color="brand.grayDark"
        :pl="{ base: 'md', md: '4xl' }"
        :pr="{ base: 'md', md: '4xl' }"
        py="3xl"
        border-radius="base"
        width="100%"
        justify-content="left"
        color="brand.purple"
      >
        <c-icon name="plus" size="1.25rem" mr="0.5rem" />
        <c-text as="span" font-weight="bold" white-space="initial" text-align="left">
          {{ $t("productReturnV2.maintenanceRequest.addSite") }}
        </c-text>
      </c-button>
    </form>
    <c-box v-if="crCount > maxCrCount" color="brand.red" font-size="sm" mt="2xl">
      {{ $t("productReturn.maintenanceRequest.tooBig", { maxSize: maxCrCount }) }}
    </c-box>
    <c-box v-if="duplicateSerials.length" color="brand.red" font-size="sm" mt="2xl">
      {{ $t("productReturn.productEditor.serialNumber.duplicatesDetected") }}
    </c-box>
  </c-box>
</template>

<script lang="ts">
import { FormValidationMixin } from "@/components/FormValidationMixin.js";
import config from "@/config";
import { CBox, CButton, CHeading, CIcon, CText } from "@chakra-ui/vue";
import { defineComponent } from "vue";
import RMACRSiteEditor from "./RMACRSiteEditor.vue";

const maxCrs = parseInt(config.get("customerReturnMaxSize"), 10) || 500;

export default defineComponent({
  components: {
    RMACRSiteEditor,
    CHeading,
    CText,
    CBox,
    CButton,
    CIcon,
  },
  mixins: [FormValidationMixin],
  props: {
    proFormaRequired: {
      type: Boolean,
      required: true,
    },
  },
  data() {
    return {
      form: {
        sites: [
          {
            $hasErrors: false,
            siteDetails: {
              name: "",
              reference: "",
            },
            products: [],
          },
        ],
      },
      formErrors: [] as boolean[],
    };
  },
  computed: {
    maxCrCount(): number {
      return maxCrs;
    },
    firstSiteExists(): boolean {
      return !!this.form.sites?.[0]?.siteDetails?.name;
    },
    crCount(): number {
      let result = 0;
      this.form.sites.forEach((site: any) => {
        site.products.forEach((product: any) => {
          result += product.count || 0;
        });
      });
      return result;
    },
    duplicateSerials(): string[] {
      const serialsAsKey: { [key: string]: boolean } = {};
      const result: string[] = [];
      this.form.sites.forEach((site) =>
        site.products.forEach((product) =>
          product.serials.forEach((serial: string) => {
            if (serial !== "") {
              if (serialsAsKey[serial]) {
                result.push(serial);
              }
              serialsAsKey[serial] = true;
            }
          })
        )
      );
      return result;
    },
  },
  methods: {
    beautify(text: string): string {
      let t = text.replace(/->/g, "→");
      t = t.replace(/ *, */g, ", ");
      t = t.replace(/ *\. */g, ". ");
      return t.replace(/ *→ */g, " → ").trim();
    },
    addNewSite(): void {
      this.form.sites.push({
        $hasErrors: false,
        siteDetails: {
          name: "",
          reference: "",
        },
        products: [],
      });
    },
    onRemove(index: number): void {
      this.form.sites?.splice(index - 1, 1);
      this.formErrors.splice(index - 1, 1);
      this.checkErrors();
    },
    onErrors(index: number, ev: boolean): void {
      this.formErrors[index - 1] = ev;
      if (this.form.sites?.[index - 1]) {
        this.form.sites[index - 1].$hasErrors = ev;
      }
      this.checkErrors();
    },
    checkErrors(): void {
      if (
        this.formErrors.indexOf(true) >= 0 ||
        this.crCount > this.maxCrCount ||
        this.duplicateSerials.length
      ) {
        this.$emit("errors", true);
      } else {
        this.$emit("errors", false);
      }
    },
    submit(): void {
      // Do nothing
    },
  },
  watch: {
    crCount() {
      this.checkErrors();
    },
    duplicateSerials() {
      this.checkErrors();
    },
  },
});
</script>
